import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './OffersGrid.css';
import { images } from '../../shared/assets';
import { useBrandHomePageOffers } from '../../../hooks/useBrandHomePageContent';
import { HandDrawnButton } from '../../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../shared/CTALink/CTALink';

interface OfferData {
  id: number;
  image: string;
  tag: string;
  title: string;
  link: string;
}

// Move offers array inside component to use translations
const useOffers = () => {
  const { t } = useTranslation();
  
  return [
    {
      id: 1,
      image: images.brandHomePage.offers.offer1,
      tag: t('pages/home:offers.tag'),
      title: t('pages/home:offers.items.combine.title'),
      link: "/packages/combine-three"
    },
    {
      id: 2,
      image: images.brandHomePage.offers.offer2,
      tag: t('pages/home:offers.tag'),
      title: t('pages/home:offers.items.stayLonger.title'),
      link: "/packages/stay-longer"
    },
    {
      id: 3,
      image: images.brandHomePage.offers.offer3,
      tag: t('pages/home:offers.tag'),
      title: t('pages/home:offers.items.wellness.title'),
      link: "/re-energize"
    }
  ];
};

interface TagProps {
  text: string;
}

const Tag: React.FC<TagProps> = ({ text }) => {
  return (
    <div className="offers-grid__tag">
      <div className="offers-grid__tag-border" />
      <div className="offers-grid__tag-text">
        <p>{text}</p>
      </div>
    </div>
  );
};

interface OfferCardProps {
  offer: OfferData;
}

const OfferCard: React.FC<OfferCardProps> = ({ offer }) => {
  const navigate = useNavigate();
  
  return (
    <div className="offers-grid__card">
      <div className="offers-grid__card-image-container">
        <img
          src={offer.image}
          alt={offer.title}
          className="offers-grid__card-image"
        />
        <div className="offers-grid__card-tag">
          <Tag text={offer.tag} />
        </div>
      </div>
      <div className="offers-grid__card-content">
        <div className="offers-grid__card-title">
          <p>{offer.title}</p>
        </div>
        <div className="offers-grid__card-link-container">
          <CTALink 
            text="VIEW MORE"
            type="secondary"
            color="awasi"
            onClick={() => navigate(offer.link)}
            className="offers-grid__link"
          />
        </div>
      </div>
    </div>
  );
};

export const OffersGrid: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const offers = useOffers();
  const { offers: contentfulOffers, loading } = useBrandHomePageOffers();

  // Use Contentful offers if available, otherwise use static offers
  const displayOffers = contentfulOffers.length > 0 
    ? contentfulOffers.map((offer, index) => ({
        id: index + 1,
        image: offer.image || offers[index]?.image || '',
        tag: offers[index]?.tag || "AWASI",
        title: String(offer.title),
        link: offers[index]?.link || "/packages"
      }))
    : offers;

  return (
    <section className="offers-grid" data-scroll-section>
      <div className="offers-grid__packages">
        {displayOffers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} />
        ))}
      </div>
      <div className="offers-grid__text">
        <div className="offers-grid__text-title">
          <p>{String(t('pages/home:offers.section.title')).split(' ').map((word, index, arr) => (
            <React.Fragment key={index}>
              {word}{' '}
              {index === 5 && <br />}
            </React.Fragment>
          ))}</p>
        </div>
        <div className="offers-grid__text-content">
          <div className="offers-grid__text-description">
            <p>
              {t('pages/home:offers.section.description')}
            </p>
          </div>
          <HandDrawnButton
            text={t('pages/home:offers.section.cta')}
            onClick={() => navigate('/packages')}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            size="normal"
            className="offers-grid__button"
          />
        </div>
      </div>
    </section>
  );
};